// smartSilenceDetector.js
//
// Enterprise-grade silence detection for the screening interview.
//
// Primary mode: Silero VAD (Voice Activity Detection) via @ricky0123/vad-web.
// The Silero neural net decides whether a human is speaking every ~96ms, so
// natural mid-answer pauses ("uhh...", breath, thinking) never trip the timer.
//
// Fallback mode: RMS-energy threshold (the old behaviour). Kicks in if the
// VAD model / WASM fails to load, or if REACT_APP_USE_SMART_VAD=false.
//
// Asset files are served from /vad/ (self-hosted in frontend/public/vad/) so
// the app never depends on the ricky0123 CDN.

import { MicVAD } from '@ricky0123/vad-web';

const VAD_ASSET_PATH = '/vad/';

// VAD tuning — chosen to be forgiving of natural pauses in interview answers.
// See README.md in this folder for the reasoning behind each threshold.
const DEFAULT_VAD_CONFIG = {
  positiveSpeechThreshold: 0.5,
  negativeSpeechThreshold: 0.35,
  redemptionFrames: 8,       // ~250 ms of silence before onSpeechEnd fires
  frameSamples: 1536,        // 96 ms per frame at 16 kHz
  preSpeechPadFrames: 10,
  minSpeechFrames: 3,        // ignore <300 ms blips (coughs, clicks)
};

const DEFAULT_RMS_CONFIG = {
  threshold: 0.05,
  pollMs: 200,
};

// Behaviour thresholds — the four safety rules from the design.
const DEFAULT_BEHAVIOUR = {
  silenceMs: 2500,           // Rule 3: continuous silence before firing
  minSpeechMs: 500,          // Rule 1: minimum speech before silence counts
  extraGraceMs: 2500,        // Rule 4: extra grace after setSilenceMs bumps
};

const log = (...args) => {
  if (typeof console !== 'undefined') console.log('[vad]', ...args);
};
const warn = (...args) => {
  if (typeof console !== 'undefined') console.warn('[vad]', ...args);
};
const error = (...args) => {
  if (typeof console !== 'undefined') console.error('[vad]', ...args);
};

/**
 * SmartSilenceDetector — replaces the RMS-timer silence detection.
 *
 * @param {Object} options
 * @param {MediaStream} [options.stream]         Optional existing mic stream to reuse (recommended).
 * @param {AnalyserNode} [options.analyser]      Required only for RMS fallback.
 * @param {number} [options.silenceMs=2500]      Silence ms after speech ends before onSilence fires.
 * @param {number} [options.minSpeechMs=500]     Minimum captured speech before silence counts.
 * @param {Function} options.onSilence           Callback fired when true silence is confirmed.
 * @param {Function} [options.onSpeechStart]     Optional: fired when speech is detected.
 * @param {Function} [options.onSpeechEnd]       Optional: fired when speech ends (before silence timer).
 * @param {Function} [options.onReady]           Optional: fired once detector is loaded ('vad' | 'rms').
 * @param {Function} [options.onFallback]        Optional: fired if VAD load fails and RMS takes over.
 */
class SmartSilenceDetector {
  constructor(options = {}) {
    this.stream = options.stream || null;
    this.analyser = options.analyser || null;
    this.onSilence = options.onSilence || (() => {});
    this.onSpeechStart = options.onSpeechStart || (() => {});
    this.onSpeechEnd = options.onSpeechEnd || (() => {});
    this.onReady = options.onReady || (() => {});
    this.onFallback = options.onFallback || (() => {});

    this._silenceMs = options.silenceMs ?? DEFAULT_BEHAVIOUR.silenceMs;
    this._minSpeechMs = options.minSpeechMs ?? DEFAULT_BEHAVIOUR.minSpeechMs;

    this._vad = null;
    this._rmsInterval = null;
    this._silenceTimeout = null;
    this._rmsSilenceStart = null;
    this._speechSeenAt = 0;
    this._speaking = false;
    this._active = false;
    this._paused = false;
    this._mode = null; // 'vad' | 'rms' | null
  }

  /**
   * Starts the detector. Tries Silero VAD first; falls back to RMS if VAD
   * fails to load (missing WASM, unsupported browser, etc.).
   */
  async start() {
    if (this._active) {
      warn('start() called but detector already active — ignoring');
      return;
    }
    this._active = true;
    this._paused = false;

    const useSmartVAD = process.env.REACT_APP_USE_SMART_VAD !== 'false';

    if (useSmartVAD) {
      try {
        await this._startVAD();
        this._mode = 'vad';
        log('mode=vad ready, silenceMs=' + this._silenceMs);
        this.onReady('vad');
        return;
      } catch (err) {
        error('VAD failed to initialise, falling back to RMS:', err?.message || err);
        try { this.onFallback(err); } catch (e) { /* swallow */ }
      }
    }

    this._startRMS();
    this._mode = 'rms';
    log('mode=rms (fallback) ready, silenceMs=' + this._silenceMs);
    this.onReady('rms');
  }

  async _startVAD() {
    if (!this.stream) {
      throw new Error('SmartSilenceDetector: stream is required for VAD mode');
    }
    // MicVAD.new resolves once the ONNX model + audio worklet are loaded and
    // running. Failures here (e.g. WASM 404, worklet syntax error, model
    // load timeout) propagate to start() and trigger the RMS fallback.
    this._vad = await MicVAD.new({
      stream: this.stream,
      positiveSpeechThreshold: DEFAULT_VAD_CONFIG.positiveSpeechThreshold,
      negativeSpeechThreshold: DEFAULT_VAD_CONFIG.negativeSpeechThreshold,
      redemptionFrames: DEFAULT_VAD_CONFIG.redemptionFrames,
      frameSamples: DEFAULT_VAD_CONFIG.frameSamples,
      preSpeechPadFrames: DEFAULT_VAD_CONFIG.preSpeechPadFrames,
      minSpeechFrames: DEFAULT_VAD_CONFIG.minSpeechFrames,
      baseAssetPath: VAD_ASSET_PATH,
      onnxWASMBasePath: VAD_ASSET_PATH,
      onSpeechStart: () => this._handleSpeechStart(),
      onSpeechEnd: (audio) => this._handleSpeechEnd(audio),
      onVADMisfire: () => log('misfire (sub-threshold blip ignored)'),
    });

    this._vad.start();
  }

  _handleSpeechStart() {
    this._speaking = true;
    this._speechSeenAt = Date.now();
    // Rule 1: any speech immediately cancels the pending silence timer.
    this._cancelSilenceTimer();
    log('speech start');
    try { this.onSpeechStart(); } catch (e) { error('onSpeechStart threw:', e); }
  }

  _handleSpeechEnd(audio) {
    this._speaking = false;
    log('speech end, starting silence timer:', this._silenceMs, 'ms');
    try { this.onSpeechEnd(audio); } catch (e) { error('onSpeechEnd threw:', e); }
    this._startSilenceTimer();
  }

  _startSilenceTimer() {
    if (this._paused || !this._active) return;
    this._cancelSilenceTimer();

    // Rule 2: don't fire until candidate has spoken at least minSpeechMs.
    // Guards against firing on background clicks/coughs before any real
    // speech has happened.
    const speechDuration = this._speechSeenAt ? Date.now() - this._speechSeenAt : 0;
    if (speechDuration < this._minSpeechMs) {
      log('silence timer skipped — only', speechDuration, 'ms of speech (need', this._minSpeechMs, ')');
      return;
    }

    this._silenceTimeout = setTimeout(() => {
      this._silenceTimeout = null;
      log('SILENCE CONFIRMED after', this._silenceMs, 'ms — firing onSilence');
      try { this.onSilence(); } catch (e) { error('onSilence threw:', e); }
    }, this._silenceMs);
  }

  _cancelSilenceTimer() {
    if (this._silenceTimeout) {
      clearTimeout(this._silenceTimeout);
      this._silenceTimeout = null;
    }
  }

  _startRMS() {
    // Fallback path — the original RMS-energy silence detection, kept only
    // so a VAD-load failure doesn't brick the interview.
    if (!this.analyser) {
      warn('RMS fallback needs an AnalyserNode; supplying one via options.analyser');
      return;
    }

    const buffer = new Float32Array(this.analyser.fftSize);

    this._rmsInterval = setInterval(() => {
      if (this._paused || !this._active || !this.analyser) return;

      this.analyser.getFloatTimeDomainData(buffer);
      let sumSquares = 0;
      for (let i = 0; i < buffer.length; i++) sumSquares += buffer[i] * buffer[i];
      const rms = Math.sqrt(sumSquares / buffer.length);
      const now = Date.now();

      if (rms < DEFAULT_RMS_CONFIG.threshold) {
        if (!this._rmsSilenceStart) {
          this._rmsSilenceStart = now;
        } else if (now - this._rmsSilenceStart >= this._silenceMs) {
          const speechDuration = this._speechSeenAt ? now - this._speechSeenAt : 0;
          if (speechDuration < this._minSpeechMs) return;
          this._rmsSilenceStart = null;
          log('[rms] silence timeout — firing onSilence');
          try { this.onSilence(); } catch (e) { error('onSilence threw:', e); }
        }
      } else {
        this._rmsSilenceStart = null;
        if (!this._speechSeenAt) this._speechSeenAt = now;
      }
    }, DEFAULT_RMS_CONFIG.pollMs);
  }

  /**
   * Dynamically update the silence threshold. Used by the "more time" flow
   * to bump 2.5s → 5s after granting extra time (Rule 4).
   */
  setSilenceMs(ms) {
    const n = Math.max(500, Math.min(30000, Number(ms) || DEFAULT_BEHAVIOUR.silenceMs));
    this._silenceMs = n;
    log('silenceMs updated to', n);
  }

  setMinSpeechMs(ms) {
    const n = Math.max(0, Math.min(10000, Number(ms) || DEFAULT_BEHAVIOUR.minSpeechMs));
    this._minSpeechMs = n;
  }

  /**
   * Update the onSilence callback without restarting the detector. Cheaper
   * than tear-down/rebuild between questions.
   */
  setOnSilence(cb) {
    this.onSilence = typeof cb === 'function' ? cb : (() => {});
  }

  /**
   * Reset the "already saw speech" state, e.g. at the start of a new
   * question so the previous answer's speech doesn't satisfy Rule 2.
   */
  resetSpeechState() {
    this._speechSeenAt = 0;
    this._speaking = false;
    this._cancelSilenceTimer();
  }

  pause() {
    if (!this._active) return;
    this._paused = true;
    this._cancelSilenceTimer();
    if (this._vad) {
      try { this._vad.pause(); } catch (e) { /* swallow */ }
    }
    log('paused');
  }

  resume() {
    if (!this._active) return;
    this._paused = false;
    if (this._vad) {
      try { this._vad.start(); } catch (e) { /* swallow */ }
    }
    log('resumed');
  }

  isSpeaking() {
    return this._speaking;
  }

  getMode() {
    return this._mode;
  }

  stop() {
    this._active = false;
    this._paused = false;
    this._cancelSilenceTimer();

    if (this._rmsInterval) {
      clearInterval(this._rmsInterval);
      this._rmsInterval = null;
    }

    if (this._vad) {
      try {
        this._vad.pause();
        if (typeof this._vad.destroy === 'function') this._vad.destroy();
      } catch (e) {
        warn('error stopping VAD:', e?.message || e);
      }
      this._vad = null;
    }

    log('stopped');
  }
}

export default SmartSilenceDetector;
export { DEFAULT_VAD_CONFIG, DEFAULT_RMS_CONFIG, DEFAULT_BEHAVIOUR };
